import { divInt, sub, toPixels } from "../shared/fixed";
import type { AbilityDef, MatchRules } from "./ability";
import { runAction } from "./actions";
import { currentDirection, directionMatches, gestureWithoutMeter, pickAbility, PRESS_BUFFER } from "./command";
import type { SimEvent } from "./events";
import { ATTACK_BUTTONS, BUTTON, type InputBits } from "./input";
import { FIGHTER, HISTORY, PRESS_NEVER, type FighterState, type FighterStateId } from "./state";
import { FULL_METER, trySuper, updateOpener } from "./super";
import { canGrab, startThrow } from "./throw";
import { reactionLean } from "./reaction";
import { setState } from "./transition";
import { TURN_FRAMES } from "./turn";

/**
 * Сбитый не лежит (Джон 04.11.2026: «не должны падать с паузой… кувырок со скольжением… сразу круто переходит в
 * бой»): упал — кувырок по полу столько кадров и сразу стойка. Ввод выбирает, как встать (ROLL_CHOICE).
 */
export const KNOCKDOWN_FRAMES = 22;
/** На каком кадре падения читается ввод: вперёд, назад, вверх, удар. */
const ROLL_CHOICE = 2;
/** Скорость кувырка по полу, пикселей за кадр, и сколько кадров он катится. */
const ROLL_SPEED = 6;
const ROLL_FRAMES = 16;
/** Нажатие удара за столько кадров до падения ещё засчитывается — атака с пола. */
const RISE_BUFFER = 12;
/** Сколько ударов можно связать отменами в одну цепочку. */
export const CHAIN_LIMIT = 4;
/** Через сколько кадров полёта сбитый может выровняться (Джон: в воздухе не беспомощен). */
export const AIR_RECOVERY_FRAMES = 20;

/** Бег быстрее шага во столько раз. */
const RUN_FACTOR = 2.3;
/** Удар на ходу: «вперёд» во время удара тянет бойца на эту долю шага, «назад» — на эту долю шага назад. */
const STEER_FORWARD = 0.55;
const STEER_BACK = 0.45;

/** Двойное «вперёд»: вперёд → отпустил → снова вперёд за 14 кадров. */
function doubleForward(fighter: FighterState): boolean {
  let phase = 0;
  for (let age = 0; age < 14; age++) {
    const direction = fighter.history[(fighter.historyHead - age + HISTORY) % HISTORY] ?? 0;
    const forward = directionMatches(direction, "F");
    if (phase === 0 && forward) phase = 1;
    else if (phase === 1 && !forward) phase = 2;
    else if (phase === 2 && forward) return true;
  }
  return false;
}
/** С какой скорости сбитого отбрасывает от стены, пикселей за кадр. */
const WALL_BOUNCE_MIN = 4;
export const GETUP_FRAMES = 22;
const FRICTION = sub(0.45);

const FREE_STATES: readonly FighterStateId[] = [
  FIGHTER.idle,
  FIGHTER.walkForward,
  FIGHTER.walkBack,
  FIGHTER.crouch,
  FIGHTER.block,
  FIGHTER.crouchBlock,
];

export const isFree = (fighter: FighterState): boolean => FREE_STATES.includes(fighter.state);
export const isAirborne = (fighter: FighterState): boolean => fighter.y < 0 || fighter.vy < 0;

export { setState } from "./transition";

/**
 * Приём начат — и событие о нём. Любой путь: с места, продолжение серии, отмена, заряд, в прыжке — журнал «чёрного
 * ящика» и счётчик приёмов в бою ботов видят каждый.
 */
function begin(
  fighter: FighterState,
  index: number,
  rules: MatchRules,
  side: 0 | 1,
  frame: number,
  emit: (e: SimEvent) => void,
): void {
  startAbility(fighter, index, rules, side);
  const def = rules.kits[side][index];
  emit({
    kind: "ability",
    frame,
    who: side,
    x: toPixels(fighter.x),
    y: -74,
    strength: 0,
    ability: def?.id ?? "",
    ...(def?.fx?.start ? { fx: def.fx.start } : {}),
  });
}

function startAbility(fighter: FighterState, index: number, rules: MatchRules, side: 0 | 1): void {
  const ability = rules.kits[side][index];
  if (!ability) return;
  // удар с ходу: разгон шага или бега не пропадает, а гаснет трением — боец не замирает на месте
  const hero = rules.heroes[side];
  const carried =
    fighter.state === FIGHTER.walkForward
      ? sub(hero.walkSpeed * (fighter.running ? RUN_FACTOR : 1))
      : fighter.state === FIGHTER.walkBack
        ? -sub(hero.backSpeed)
        : 0;
  if (carried !== 0 && !isAirborne(fighter)) fighter.vx = carried * fighter.facing;
  setState(fighter, FIGHTER.ability);
  fighter.ability = index;
  // шаг вдогонку — только у продолжения серии (chase ставит его после начала удара)
  fighter.chase = 0;
  fighter.abilityFrame = 0;
  fighter.hasHit = 0;
  fighter.meter -= ability.cost ?? 0;
}

/** Свободный герой на земле: способность, прыжок, блок, присед, шаг — по приоритету. */
function freeGround(
  fighter: FighterState,
  other: FighterState,
  bits: InputBits,
  rules: MatchRules,
  side: 0 | 1,
  frame: number,
  emit: (e: SimEvent) => void,
  buffer = PRESS_BUFFER,
): void {
  if (trySuper(fighter, rules, side, frame, emit)) return;
  if (tryGrab(fighter, other, rules, side, frame, emit)) return;
  if (tryTaunt(fighter, rules, side, frame, emit)) return;
  const hero = rules.heroes[side];
  // окно серии после удара: нажатие сразу после конца удара — следующий удар ветки, а не снова первый
  const last = fighter.comboLeft > 0 ? rules.kits[side][fighter.comboAbility] : undefined;
  const series = last && fighter.chain < CHAIN_LIMIT ? seriesStep(fighter, last, rules.kits[side], PRESS_BUFFER) : null;
  if (fighter.comboLeft > 0) fighter.comboLeft--;
  const distance = Math.abs(other.x - fighter.x) / 256;
  const ability = series ? series.index : pickAbility(fighter, rules.kits[side], false, undefined, distance, buffer);
  if (series) {
    fighter.pressAge[series.button] = PRESS_NEVER;
    fighter.chain++;
  } else if (ability >= 0) fighter.chain = 0;
  if (ability < 0) {
    const wanted = gestureWithoutMeter(fighter, rules.kits[side], false);
    if (wanted)
      emit({
        kind: "action",
        frame,
        who: side,
        x: toPixels(fighter.x),
        y: -90,
        strength: 0,
        fx: "rage/empty",
        ability: wanted,
      });
  }
  if (ability >= 0) {
    fighter.comboLeft = 0;
    begin(fighter, ability, rules, side, frame, emit);
    return;
  }
  const direction = currentDirection(fighter);
  const down = directionMatches(direction, "D");
  if (directionMatches(direction, "U")) {
    setState(fighter, FIGHTER.jump);
    fighter.vy = -sub(hero.jumpSpeed);
    const forward = directionMatches(direction, "F") ? 1 : directionMatches(direction, "B") ? -1 : 0;
    fighter.vx = forward * sub(hero.jumpForward) * fighter.facing;
    return;
  }
  const next: FighterStateId =
    (bits & BUTTON.block) !== 0
      ? down
        ? FIGHTER.crouchBlock
        : FIGHTER.block
      : down
        ? FIGHTER.crouch
        : directionMatches(direction, "F")
          ? FIGHTER.walkForward
          : directionMatches(direction, "B")
            ? FIGHTER.walkBack
            : FIGHTER.idle;
  if (next !== fighter.state) setState(fighter, next);
  if (next !== FIGHTER.walkForward) fighter.running = 0;
  else if (!fighter.running && doubleForward(fighter)) fighter.running = 1;
  if (next === FIGHTER.walkForward)
    fighter.vx = sub(hero.walkSpeed * (fighter.running ? RUN_FACTOR : 1)) * fighter.facing;
  else if (next === FIGHTER.walkBack) fighter.vx = -sub(hero.backSpeed) * fighter.facing;
}

/**
 * Шаг продолжения серии (консультант 05.11.2026: «в файтингах продолжение серии гарантированно достаёт… шагом
 * продолжения»): соперника уносит отбросом прошлого удара и тело откинуто реакцией — с началом следующего удара серии
 * боец идёт вдогонку до зазора, с которого удар ложится на тело с запасом CHASE_MARGIN (fighter.chase — этот зазор),
 * не быстрее CHASE_SPEED и не ближе (иначе толкал бы соперника дальше). Целые подпиксели — онлайн одинаково.
 */
const CHASE_MARGIN = 12;
const CHASE_SPEED = 11;
const BODY_HALF = 27;

function chase(fighter: FighterState, other: FighterState, rules: MatchRules, side: 0 | 1): void {
  fighter.chase = 0;
  const ability = rules.kits[side][fighter.ability];
  const box = ability?.hitbox;
  if (!ability || !box || isAirborne(fighter) || isAirborne(other)) return;
  // тело соперника в реакции к первому активному кадру удара: откинуто назад — зазор нужен меньше
  const later = { ...other, stateFrame: other.stateFrame + ability.startup };
  const lean = reactionLean(later, rules.heroes[side === 0 ? 1 : 0].reactionBody);
  fighter.chase = Math.max(1, sub(box.x + box.w + BODY_HALF + lean - CHASE_MARGIN));
}

/** Удар на земле в движении: подача приёма и направление игрока задают скорость (кадры мира, без своего времени). */
function steer(fighter: FighterState, other: FighterState, ability: AbilityDef, rules: MatchRules, side: 0 | 1): void {
  const hero = rules.heroes[side];
  const direction = currentDirection(fighter);
  let forward = fighter.vx * fighter.facing;
  if (ability.advance && !ability.motion && fighter.abilityFrame < ability.startup + ability.active)
    forward = Math.max(forward, sub(ability.advance));
  // шаг продолжения серии — пока зазор больше нужного (chase), до конца активных кадров
  if (fighter.chase > 0 && fighter.abilityFrame < ability.startup + ability.active) {
    const excess = Math.abs(other.x - fighter.x) - fighter.chase;
    if (excess > 0) forward = Math.max(forward, Math.min(sub(CHASE_SPEED), excess));
  }
  if (directionMatches(direction, "F")) forward = Math.max(forward, sub(hero.walkSpeed * STEER_FORWARD));
  else if (directionMatches(direction, "B") && !ability.holdsGround)
    forward = Math.min(forward, -sub(hero.backSpeed * STEER_BACK));
  fighter.vx = forward * fighter.facing;
}

/** Вторая половина восстановления на земле: направление отменяет остаток удара — сразу шаг, бег или прыжок. */
function cancelledByMove(fighter: FighterState, ability: AbilityDef): boolean {
  if (isAirborne(fighter)) return false;
  if (fighter.abilityFrame < ability.startup + ability.active + divInt(ability.recovery, 2)) return false;
  const direction = currentDirection(fighter);
  if (ability.holdsGround && directionMatches(direction, "B")) return false;
  return directionMatches(direction, "F") || directionMatches(direction, "B") || directionMatches(direction, "U");
}

function runAbility(
  fighter: FighterState,
  other: FighterState,
  rules: MatchRules,
  side: 0 | 1,
  frame: number,
  emit: (e: SimEvent) => void,
): void {
  const ability = rules.kits[side][fighter.ability];
  if (!ability) {
    setState(fighter, FIGHTER.idle);
    return;
  }
  // две кнопки вместе: вторая пришла через кадр-другой после первой — удар по первой меняется на суперсилу
  const ownButton = ability.input.button;
  if (ownButton && fighter.abilityFrame < 3 && fighter.chain === 0) {
    const pair = pickAbility(fighter, rules.kits[side], isAirborne(fighter), {
      button: ownButton,
      age: fighter.abilityFrame,
    });
    if (pair >= 0) {
      begin(fighter, pair, rules, side, frame, emit);
      return;
    }
  }
  // заряд отпустили, пока идёт восстановление удара по той же кнопке, — заряженный удар выходит сразу, не пропадает
  if (fighter.abilityFrame >= ability.startup + ability.active) {
    const charged = rules.kits[side].findIndex(
      (candidate) =>
        candidate.input.charge !== undefined &&
        candidate.input.button !== undefined &&
        (candidate.input.air ?? false) === isAirborne(fighter) &&
        -(fighter.held[ATTACK_BUTTONS.indexOf(candidate.input.button)] ?? 0) >= candidate.input.charge,
    );
    if (charged >= 0) {
      begin(fighter, charged, rules, side, frame, emit);
      return;
    }
  }
  // дерево комбо: любая кнопка в окне серии — следующий удар своей ветки, попал удар или нет; нажатие за время удара
  // запоминается (живой палец жмёт и раньше окна)
  const series = seriesStep(fighter, ability, rules.kits[side], Math.min(SERIES_BUFFER, fighter.abilityFrame - 1));
  if (series && fighter.abilityFrame >= ability.startup + ability.active && fighter.chain < CHAIN_LIMIT) {
    fighter.pressAge[series.button] = PRESS_NEVER;
    fighter.chain++;
    begin(fighter, series.index, rules, side, frame, emit);
    chase(fighter, other, rules, side);
    return;
  }
  // цепочка с отменой: попал — следующий удар выходит сразу, до CHAIN_LIMIT в серии; кнопка, у которой есть ветка
  // дерева, — за серией (ждёт конца активных кадров), иначе серия сбивалась бы обратно на первый удар
  const seriesPress = series !== null;
  if (fighter.hasHit && !seriesPress && fighter.abilityFrame >= ability.startup && fighter.chain < CHAIN_LIMIT) {
    const next = pickAbility(fighter, rules.kits[side], isAirborne(fighter));
    if (next >= 0) {
      // после финала ветки удар-отмена начинает новую серию (дерево снова с корня), иначе цепочка растёт
      fighter.chain = ability.finale ? 0 : fighter.chain + 1;
      begin(fighter, next, rules, side, frame, emit);
      chase(fighter, other, rules, side);
      return;
    }
  }
  // прерывание (Джон 05.11.2026, 00:06: «не нравится когда нельзя прервать анимацию»): после окна приёма любой другой
  // приём выходит сразу; нажатое раньше окна ждёт в буфере и срабатывает на первом кадре, где можно
  const cancelAt = cancelFrame(ability);
  if (cancelAt !== undefined && fighter.abilityFrame >= cancelAt && !isAirborne(fighter)) {
    // захват тоже прерывает конец удара: удар — и сразу хват
    if (tryGrab(fighter, other, rules, side, frame, emit)) return;
  }
  if (cancelAt !== undefined && fighter.abilityFrame >= cancelAt && fighter.chain < CHAIN_LIMIT) {
    const distance = Math.abs(other.x - fighter.x) / 256;
    const buffer = Math.min(CANCEL_BUFFER, Math.max(PRESS_BUFFER, fighter.abilityFrame - 1));
    const next = pickAbility(fighter, rules.kits[side], isAirborne(fighter), undefined, distance, buffer);
    if (next >= 0 && next !== fighter.ability) {
      fighter.chain = ability.finale ? 0 : fighter.chain + 1;
      begin(fighter, next, rules, side, frame, emit);
      return;
    }
  }
  for (const cue of ability.actions ?? []) {
    if (cue.frame === fighter.abilityFrame)
      runAction(cue.type, { self: fighter, other, side, rules, frame, emit }, cue.params ?? {});
  }
  if (!isAirborne(fighter)) {
    steer(fighter, other, ability, rules, side);
    // ход корня клипа: стопа стоит, тело шагает вместе с клипом (Джон: «бьёт и при этом не шагает, а скользит»)
    const step = ability.motion?.[fighter.abilityFrame];
    if (step) fighter.x += sub(step) * fighter.facing;
  }
  fighter.abilityFrame++;
  if (
    fighter.abilityFrame >= ability.startup + ability.active + ability.recovery ||
    cancelledByMove(fighter, ability)
  ) {
    // издёвка доиграна — простой считается заново: следующая не идёт встык (консультант 05.11.2026)
    if (ability.input.taunt) fighter.quiet = 0;
    // удар с ветками кончился — серия ещё ждёт следующего нажатия COMBO_GRACE кадров
    const branching =
      (ability.branches !== undefined ||
        ability.next !== undefined ||
        ability.backBranches !== undefined ||
        ability.downBranches !== undefined) &&
      fighter.chain < CHAIN_LIMIT;
    fighter.comboAbility = branching ? fighter.ability : -1;
    fighter.comboLeft = branching ? COMBO_GRACE : 0;
    fighter.ability = -1;
    setState(fighter, isAirborne(fighter) ? FIGHTER.jump : FIGHTER.idle);
  }
}

/** Нокаут: подскок от пола — с такой скорости падения, такая доля скорости, назад столько пикселей за кадр. */
const KO_BOUNCE_MIN = 4;
const KO_BOUNCE_PERCENT = 30;
const KO_BOUNCE_SLIDE = 2;
/** fallKind нокаутированного, который уже подскочил. */
export const KO_BOUNCED = 2;

/** Физика: гравитация, приземление, трение, края арены. */
function move(fighter: FighterState, rules: MatchRules, side: 0 | 1, frame: number, emit: (e: SimEvent) => void): void {
  const hero = rules.heroes[side];
  if (isAirborne(fighter)) {
    fighter.vy += sub(hero.gravity);
    fighter.y += fighter.vy;
    fighter.x += fighter.vx;
    if (fighter.y >= 0) {
      const impact = fighter.vy;
      fighter.y = 0;
      fighter.vy = 0;
      fighter.vx = 0;
      const knockedOut = fighter.state === FIGHTER.ko;
      emit({
        kind: "land",
        frame,
        who: side,
        x: toPixels(fighter.x),
        y: 0,
        strength: fighter.state === FIGHTER.airHit || knockedOut ? 2 : 0,
      });
      // нокаут: тело бьётся о пол и подскакивает один раз, чуть проскальзывая назад (консультант 05.11.2026: «тяжёлое
      // падение, отскок и пыль»)
      if (knockedOut && fighter.fallKind !== KO_BOUNCED && impact >= sub(KO_BOUNCE_MIN)) {
        fighter.fallKind = KO_BOUNCED;
        fighter.vy = -divInt(impact * KO_BOUNCE_PERCENT, 100);
        fighter.vx = -fighter.facing * sub(KO_BOUNCE_SLIDE);
      }
      if (fighter.state === FIGHTER.airHit) setState(fighter, FIGHTER.knockdown);
      else if (fighter.state === FIGHTER.jump || fighter.state === FIGHTER.ability) {
        fighter.ability = -1;
        setState(fighter, FIGHTER.idle);
      }
    }
  } else {
    fighter.x += fighter.vx;
    const walking = fighter.state === FIGHTER.walkForward || fighter.state === FIGHTER.walkBack;
    if (walking) fighter.vx = 0;
    else fighter.vx = fighter.vx > 0 ? Math.max(0, fighter.vx - FRICTION) : Math.min(0, fighter.vx + FRICTION);
  }
  const half = sub(hero.width / 2);
  const low = sub(rules.arena.left) + half;
  const high = sub(rules.arena.right) - half;
  // сбитый в воздухе отскакивает от стены обратно в бой (Джон: отскок от стены)
  if (
    fighter.state === FIGHTER.airHit &&
    (fighter.x < low || fighter.x > high) &&
    Math.abs(fighter.vx) > sub(WALL_BOUNCE_MIN)
  ) {
    fighter.vx = -divInt(fighter.vx, 2);
    emit({
      kind: "land",
      frame,
      who: side,
      x: toPixels(fighter.x < low ? low : high),
      y: toPixels(fighter.y),
      strength: 1,
    });
  }
  fighter.x = Math.min(Math.max(fighter.x, low), high);
}

/** Нажатое во время оглушения, блока или конца приёма выходит первым кадром свободы, если нажато не раньше стольких кадров. */
const FREED_BUFFER = 12;
/** Нажатие за время приёма ждёт окна прерывания до стольких кадров. */
const CANCEL_BUFFER = 20;

/**
 * С какого кадра приём прерывается другим: из данных (cancelFrom) или — у удара — сразу после активных кадров (удар уже
 * долетел, восстановление можно отменить). Приём без удара (скачок, невидимка, бросок) сам по себе не прерывается.
 */
function cancelFrame(ability: AbilityDef): number | undefined {
  if (ability.cancelFrom !== undefined) return ability.cancelFrom;
  if (ability.input.grab || ability.input.taunt) return undefined;
  return ability.hitbox || ability.projectile ? ability.startup + ability.active : undefined;
}

/** Нажатие за время удара ждёт окна серии до стольких кадров (живой палец жмёт раньше окна). */
const SERIES_BUFFER = 30;
/** Окно серии после конца удара, кадров: ~0,4 с — серия идёт и при 4 нажатиях в секунду. */
export const COMBO_GRACE = 24;

/**
 * Следующий удар дерева комбо: самая свежая нажатая кнопка (в буфере), у которой у текущего удара есть ветка.
 * null — ни одной такой кнопки.
 */
function seriesStep(
  fighter: FighterState,
  ability: AbilityDef,
  kit: readonly AbilityDef[],
  maxAge: number,
): { index: number; button: number } | null {
  // зажато «назад» или «вниз» — своя серия направления (backBranches, downBranches); нет её ветки — приём
  // направления (выстрел, низкий удар) через прерывание, а не ветка обычного дерева
  const direction = currentDirection(fighter);
  const held = directionMatches(direction, "D")
    ? ability.downBranches
    : directionMatches(direction, "B")
      ? ability.backBranches
      : undefined;
  const directional = directionMatches(direction, "B") || directionMatches(direction, "D");
  let best: { index: number; button: number } | null = null;
  let bestAge = maxAge + 1;
  ATTACK_BUTTONS.forEach((button, buttonIndex) => {
    const age = fighter.pressAge[buttonIndex] ?? PRESS_NEVER;
    if (age >= bestAge) return;
    const id = directional
      ? held?.[button]
      : (ability.branches?.[button] ?? (button === ability.input.button ? ability.next : undefined));
    const index = id === undefined ? -1 : kit.findIndex((candidate) => candidate.id === id);
    if (index < 0) return;
    best = { index, button: buttonIndex };
    bestAge = age;
  });
  return best;
}

/** Захват L1: достал — бросок через плечо (sim/throw.ts), нет — промах, который можно наказать. */
function tryGrab(
  fighter: FighterState,
  other: FighterState,
  rules: MatchRules,
  side: 0 | 1,
  frame: number,
  emit: (e: SimEvent) => void,
): boolean {
  if ((fighter.triggerAge[3] ?? PRESS_NEVER) > PRESS_BUFFER) return false;
  const kit = rules.kits[side];
  const hit = canGrab(fighter, other);
  const index = kit.findIndex((ability) => (hit ? ability.input.grab === true : ability.input.grabWhiff === true));
  if (index < 0) return false;
  fighter.triggerAge[3] = PRESS_NEVER;
  fighter.comboLeft = 0;
  startAbility(fighter, index, rules, side);
  if (hit) startThrow(fighter, other);
  emit({
    kind: "ability",
    frame,
    who: side,
    x: toPixels(fighter.x),
    y: -74,
    strength: 0,
    ability: kit[index]?.id ?? "",
  });
  return true;
}

/** Какая из count издёвок выходит на кадре мира frame: у обоих онлайн одна и та же; бот выбирает миг нажатия по ней. */
export const tauntSlot = (frame: number, count: number): number => Math.floor(frame / 7) % Math.max(1, count);

/** Простой без ввода, кадров, — боец сам начинает издёвку (2 с; консультант 04.11.2026). */
export const AUTO_TAUNT_FRAMES = 120;

/**
 * Издёвка: кнопка (тачпад, T) или простой 2 с в стойке. Какая из издёвок — по кадру мира (у обоих онлайн одна и та же).
 * Даёт немного Ярости, но открывает: в издёвке не закрыться.
 */
function tryTaunt(
  fighter: FighterState,
  rules: MatchRules,
  side: 0 | 1,
  frame: number,
  emit: (e: SimEvent) => void,
): boolean {
  const pressed = (fighter.triggerAge[2] ?? PRESS_NEVER) <= PRESS_BUFFER;
  const idle = fighter.state === FIGHTER.idle && fighter.quiet >= AUTO_TAUNT_FRAMES;
  if (!pressed && !idle) return false;
  const kit = rules.kits[side];
  const taunts = kit.flatMap((ability, index) => (ability.input.taunt ? [index] : []));
  const index = taunts[tauntSlot(frame, taunts.length)];
  if (index === undefined) return false;
  fighter.triggerAge[2] = PRESS_NEVER;
  fighter.quiet = 0;
  startAbility(fighter, index, rules, side);
  emit({
    kind: "ability",
    frame,
    who: side,
    x: toPixels(fighter.x),
    y: -74,
    strength: 0,
    ability: kit[index]?.id ?? "",
  });
  return true;
}

/** Каждые столько кадров удержания — новая волна частиц заряда. */
const CHARGE_PULSE = 6;
/**
 * Заряд виден только после стольких кадров удержания (Джон 04.11.2026, 22:50: «анимация всё время включается, как будто
 * я держу кнопку»): обычное нажатие длится 5–9 кадров — это удар, а не начало заряда.
 */
export const CHARGE_START = 15;

/**
 * Заряд копится: пока кнопку держат, частицы стягиваются к кулаку (удары руками) или к стопе (ногами) и растут по
 * ступеням — 1, 2, на полном заряде 3 (консультант 04.11.2026: «частицы стягиваются к кулаку или ноге, растут»).
 */
function chargeFx(
  fighter: FighterState,
  rules: MatchRules,
  side: 0 | 1,
  frame: number,
  emit: (e: SimEvent) => void,
): void {
  for (const ability of rules.kits[side]) {
    const need = ability.input.charge;
    const button = ability.input.button;
    if (need === undefined || !button) continue;
    const held = fighter.held[ATTACK_BUTTONS.indexOf(button)] ?? 0;
    if (held < CHARGE_START || held % CHARGE_PULSE !== 0) continue;
    const stage = held >= need ? 3 : held * 2 >= need ? 2 : 1;
    const punch = button === "lightPunch" || button === "heavyPunch";
    emit({
      kind: "action",
      frame,
      who: side,
      x: toPixels(fighter.x) + (punch ? 34 : -12) * fighter.facing,
      y: toPixels(fighter.y) - (punch ? 150 : 18),
      strength: stage,
      fx: `charge/${String(stage)}`,
    });
  }
}

/** Один кадр бойца: автомат состояний, затем физика. canAct — идёт ли бой (не заставка и не конец раунда). */
export function updateFighter(
  fighter: FighterState,
  other: FighterState,
  bits: InputBits,
  rules: MatchRules,
  side: 0 | 1,
  canAct: boolean,
  frame: number,
  emit: (e: SimEvent) => void,
): void {
  fighter.stateFrame++;
  if (fighter.invisible > 0) fighter.invisible--;
  if (canAct) chargeFx(fighter, rules, side, frame, emit);
  const wasFree = isFree(fighter);
  switch (fighter.state) {
    case FIGHTER.idle:
    case FIGHTER.walkForward:
    case FIGHTER.walkBack:
    case FIGHTER.crouch:
    case FIGHTER.block:
    case FIGHTER.crouchBlock:
      if (canAct) freeGround(fighter, other, bits, rules, side, frame, emit);
      else if (fighter.state !== FIGHTER.idle) setState(fighter, FIGHTER.idle);
      break;
    case FIGHTER.jump:
      if (canAct) {
        const ability = pickAbility(fighter, rules.kits[side], true);
        if (ability >= 0) begin(fighter, ability, rules, side, frame, emit);
      }
      break;
    case FIGHTER.ability: {
      const current = rules.kits[side][fighter.ability];
      // издёвка: любой ввод прерывает сразу — и уходит в дело в этом же кадре
      if (current?.input.taunt && (bits & ~BUTTON.taunt) !== 0 && canAct) {
        fighter.ability = -1;
        setState(fighter, FIGHTER.idle);
        freeGround(fighter, other, bits, rules, side, frame, emit);
        break;
      }
      if (current?.tauntMeter) fighter.meter = Math.min(FULL_METER, fighter.meter + current.tauntMeter);
      // приём, который можно прервать (бег назад): любой другой приём с cancelFrom
      if (current?.cancelFrom !== undefined && fighter.abilityFrame >= current.cancelFrom && canAct) {
        const distance = Math.abs(other.x - fighter.x) / 256;
        const next = pickAbility(fighter, rules.kits[side], isAirborne(fighter), undefined, distance);
        if (next >= 0 && next !== fighter.ability) {
          begin(fighter, next, rules, side, frame, emit);
          break;
        }
        const wanted = gestureWithoutMeter(fighter, rules.kits[side], isAirborne(fighter));
        if (wanted)
          emit({
            kind: "action",
            frame,
            who: side,
            x: toPixels(fighter.x),
            y: -90,
            strength: 0,
            fx: "rage/empty",
            ability: wanted,
          });
      }
      runAbility(fighter, other, rules, side, frame, emit);
      break;
    }
    case FIGHTER.superAttack: {
      const move = rules.supers[side];
      if (move) updateOpener(fighter, move);
      else setState(fighter, FIGHTER.idle);
      break;
    }
    case FIGHTER.hitstun:
    case FIGHTER.blockstun:
      fighter.comboLeft = 0; // сбили серию — окно серии закрыто
      if (--fighter.stun <= 0) setState(fighter, FIGHTER.idle);
      break;
    case FIGHTER.airHit:
      // выравнивание в воздухе: нажал что-нибудь после AIR_RECOVERY_FRAMES — снова в бою, можно бить и блокировать
      if (canAct && fighter.health > 0 && fighter.stateFrame >= AIR_RECOVERY_FRAMES && fighter.y < 0 && bits !== 0) {
        setState(fighter, FIGHTER.jump);
        fighter.juggle = 0;
        fighter.vx = 0;
        emit({
          kind: "action",
          frame,
          who: side,
          x: toPixels(fighter.x),
          y: toPixels(fighter.y) - 70,
          strength: 1,
          fx: "blink/spark",
        });
      }
      break;
    case FIGHTER.knockdown:
      if (canAct) riseIntoFight(fighter, rules, side, frame, emit);
      break;
    case FIGHTER.turn:
      // разворот: в конце — лицом к сопернику и сразу в бой
      fighter.vx = 0;
      if (fighter.stateFrame >= TURN_FRAMES) {
        fighter.facing = other.x > fighter.x ? 1 : other.x < fighter.x ? -1 : -fighter.facing;
        setState(fighter, FIGHTER.idle);
      }
      break;
    case FIGHTER.getup:
      if (fighter.stateFrame >= GETUP_FRAMES) {
        fighter.juggle = 0;
        setState(fighter, FIGHTER.idle);
      }
      break;
    default:
      break;
  }
  // Освободился в этом кадре — отложенное нажатие срабатывает сразу, без пустого кадра
  if (!wasFree && isFree(fighter) && canAct && !isAirborne(fighter))
    freeGround(fighter, other, bits, rules, side, frame, emit, FREED_BUFFER);
  move(fighter, rules, side, frame, emit);
}

/**
 * Подъём в бой. На кадре ROLL_CHOICE: вверх — подъём прыжком сразу в воздух; удар (нажат недавно или держат) — атака
 * с пола; вперёд — кувырок к сопернику и сквозь него; иначе — кувырок назад со скольжением. Кувырок неуязвим
 * (hurtbox), но короткий: KNOCKDOWN_FRAMES — и сразу стойка, без лёжки и без бесконечного добивания на полу.
 */
function riseIntoFight(
  fighter: FighterState,
  rules: MatchRules,
  side: 0 | 1,
  frame: number,
  emit: (e: SimEvent) => void,
): void {
  const hero = rules.heroes[side];
  if (fighter.stateFrame === ROLL_CHOICE) {
    const direction = currentDirection(fighter);
    const attack = ATTACK_BUTTONS.some(
      (_, index) => (fighter.pressAge[index] ?? PRESS_NEVER) <= RISE_BUFFER || (fighter.held[index] ?? 0) > 0,
    );
    fighter.juggle = 0;
    if (directionMatches(direction, "U")) {
      setState(fighter, FIGHTER.jump);
      fighter.vy = -sub(hero.jumpSpeed * 0.85);
      fighter.vx =
        (directionMatches(direction, "F") ? 1 : directionMatches(direction, "B") ? -1 : 0) *
        sub(hero.jumpForward) *
        fighter.facing;
      emit({ kind: "land", frame, who: side, x: toPixels(fighter.x), y: 0, strength: 1 });
      return;
    }
    const rise = rules.kits[side].findIndex((ability) => ability.id === RISE_ATTACK);
    if (attack && rise >= 0) {
      ATTACK_BUTTONS.forEach((_, index) => (fighter.pressAge[index] = PRESS_NEVER));
      begin(fighter, rise, rules, side, frame, emit);
      return;
    }
    fighter.running = directionMatches(direction, "F") ? 1 : 0;
  }
  // кувырок: вперёд — к сопернику, иначе — назад; катится ROLL_FRAMES кадров, потом стойка
  if (fighter.stateFrame < ROLL_FRAMES) fighter.vx = (fighter.running ? 1 : -1) * sub(ROLL_SPEED) * fighter.facing;
  if (fighter.stateFrame >= KNOCKDOWN_FRAMES) {
    fighter.running = 0;
    fighter.vx = 0;
    setState(fighter, FIGHTER.idle);
  }
}

/** Приём «атака с пола»: удары ногами снизу и подъём — его зовёт подъём в бой, по кнопке сам не выходит. */
export const RISE_ATTACK = "rise-kick";

/**
 * Разворот к сопернику — движение на TURN_FRAMES кадров, а не мгновенный флип (Джон 04.11.2026: «враг должен ещё
 * оставаться спиной после телепорта»). Разворачивается свободный на земле; в ударе и в воздухе — нет. Пока стоит
 * спиной, удар сзади не блокируется (combat.ts). turnLock — сколько кадров ещё не замечает ушедшего за спину.
 */
export function faceOpponent(fighter: FighterState, other: FighterState): void {
  if (fighter.turnLock > 0) {
    fighter.turnLock--;
    return;
  }
  if (!isFree(fighter) || isAirborne(fighter) || fighter.x === other.x) return;
  const toward = other.x > fighter.x ? 1 : -1;
  if (toward !== fighter.facing) setState(fighter, FIGHTER.turn);
}
